import React, { useState, useEffect, useMemo, useRef } from 'react';
import countrycode from "../../data/countrycode.json";
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { X, Hotel } from "lucide-react";
import { useDispatch, useSelector } from 'react-redux';
import { citysearch } from "./HotelSlice";
import { selectCurrentUser } from '../auth/authSlice';

const DEFAULT_DESTINATIONS = [
  { id: 100000625787, name: "MUMBAI", fullRegionName: "MUMBAI, INDIA", countryName: "INDIA", is_city: true },
  { id: 100001540388, name: "NAVI MUMBAI", fullRegionName: "NAVI MUMBAI, INDIA", countryName: "INDIA", is_city: true },
  { id: 100001026453, name: "DELHI", fullRegionName: "DELHI, INDIA", countryName: "INDIA", is_city: true },
  { id: 100001011400, name: "NEW DELHI", fullRegionName: "NEW DELHI, INDIA", countryName: "INDIA", is_city: true },
  { id: 100000094117, name: "GOA", fullRegionName: "GOA, INDIA", countryName: "INDIA", is_city: true },
  { id: 100000000474, name: "MANALI", fullRegionName: "MANALI, INDIA", countryName: "INDIA", is_city: true },
  { id: 100000631289, name: "CHENNAI", fullRegionName: "CHENNAI, INDIA", countryName: "INDIA", is_city: true },
  { id: 100000009193, name: "JAIPUR", fullRegionName: "JAIPUR, INDIA", countryName: "INDIA", is_city: true },
];

const CITY_SEARCH_DEBOUNCE_MS = 350;

const HotelSearchForm = ({ className = "", initialFormData = null }) => {
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);


  // Format date to "DD MMM YYYY"
  const formatDate = (date) => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = date.getDate();
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };
  const options = { day: '2-digit', month: 'short', year: 'numeric' };
  const formattedToday = formatDate(today);
  const formattedTomorrow = formatDate(tomorrow);


  // When `initialFormData` is supplied (e.g. prefilling "Modify Search" with
  // the current search criteria), merge it over the defaults so any missing
  // field still falls back sensibly instead of rendering blank/undefined.
  const [formData, setFormData] = useState(() => {
    if (!initialFormData) {
      return {
        city: 'MUMBAI',
        cityCode: '699261',
        is_city: true,
        checkIn: formattedToday,
        checkOut: formattedTomorrow,
        nationality: 'India',
        nationalityCode: '106',
        selectedRatings: [1, 2, 3, 4, 5],
        rooms: [
          {
            adults: 2,
            children: 0,
            childAge: []
          }
        ]
      };
    }

    return {
      HomeStayOnly: initialFormData.HomeStayOnly || false,
      city: initialFormData.city || 'MUMBAI',
      cityCode: initialFormData.cityCode || '',
      countryName: initialFormData.countryName,
      is_city: initialFormData.is_city === true || initialFormData.is_city === '1' || initialFormData.is_city === 1,
      checkIn: initialFormData.checkIn || formattedToday,
      checkOut: initialFormData.checkOut || formattedTomorrow,
      nationality: initialFormData.nationality || 'India',
      nationalityCode: initialFormData.nationalityCode || '106',
      selectedRatings: initialFormData.selectedRatings?.length ? initialFormData.selectedRatings : [1, 2, 3, 4, 5],
      rooms: initialFormData.rooms?.length ? initialFormData.rooms : [
        {
          adults: 1,
          children: 0,
          childAge: []
        }
      ]
    };
  });

  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [showNationalityDropdown, setShowNationalityDropdown] = useState(false);
  const [showGuestPopup, setShowGuestPopup] = useState(false);
  const [showCheckInCalendar, setShowCheckInCalendar] = useState(false);
  const [showCheckOutCalendar, setShowCheckOutCalendar] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [citySearchTerm, setCitySearchTerm] = useState('');
  const [nationalitySearchTerm, setNationalitySearchTerm] = useState('');
  const [selectedcountryId, setselectedcountryId] = useState('106');

  // City search results (defaults until the user types, then API-backed)
  const [filteredCities, setFilteredCities] = useState(DEFAULT_DESTINATIONS);
  const [cityLoading, setCityLoading] = useState(false);
  const citySearchPromiseRef = useRef(null);

  // Debounced city search via redux thunk (citysearch) in HotelSlice
  useEffect(() => {
    if (!citySearchTerm.trim()) {
      setFilteredCities(DEFAULT_DESTINATIONS);
      setCityLoading(false);
      return;
    }

    setCityLoading(true);

    const timer = setTimeout(() => {
      citySearchPromiseRef.current?.abort();
      const promise = dispatch(citysearch({ keyword: citySearchTerm }));
      citySearchPromiseRef.current = promise;

      promise.then((action) => {
        if (citysearch.fulfilled.match(action)) {
          const results = action.payload?.data?.data ?? action.payload?.data ?? [];
          setFilteredCities(Array.isArray(results) ? results : []);
        } else if (!action.meta?.aborted) {
          setFilteredCities([]);
        }
        setCityLoading(false);
      });
    }, CITY_SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      citySearchPromiseRef.current?.abort();
    };
  }, [citySearchTerm, dispatch]);
  // Get top 10 popular nationalities
  const popularNationalities = useMemo(() => {
    const popularCountryNames = [
      'India', 'United States', 'United Kingdom', 'Australia', 'Canada',
      'Germany', 'France', 'Japan', 'Singapore', 'United Arab Emirates'
    ];
    return countrycode
      .filter(country => popularCountryNames.some(name =>
        country.name.toLowerCase().includes(name.toLowerCase())
      ))
      .slice(0, 10);
  }, []);

  // Filter nationalities based on search term
  const filteredNationalities = useMemo(() => {
    if (!nationalitySearchTerm.trim()) {
      return popularNationalities;
    }

    const searchTerm = nationalitySearchTerm.toLowerCase();
    return countrycode
      .filter(country =>
        country.name.toLowerCase().includes(searchTerm)
      )
      .slice(0, 50);
  }, [nationalitySearchTerm, popularNationalities]);

  const ratings = [1, 2, 3, 4, 5];

  // Handle city input change
  const handleCityInputChange = (e) => {
    const value = e.target.value;
    setCitySearchTerm(value);
    setFormData(prev => ({
      ...prev,
      city: value,
      cityCode: '',
      is_city: true
    }));

    if (value.length > 0 && !showCityDropdown) {
      setShowCityDropdown(true);
    }
  };

  // Handle city selection
  const handleCitySelect = (city) => {
    setFormData(prev => ({
      ...prev,
      city: city.name,
      hotel_id: (!city.is_city) ? city.id : 0,
      cityCode: city.id.toString(),
      countryName: city.countryName,
      is_city: !!city.is_city
    }));
    setCitySearchTerm('');
    setShowCityDropdown(false);
  };

  // Handle city input focus
  const handleCityFocus = () => {
    setShowCityDropdown(true);
    setCitySearchTerm('');
  };

  // Handle nationality input change
  const handleNationalityInputChange = (e) => {
    const value = e.target.value;
    setNationalitySearchTerm(value);
    setFormData(prev => ({
      ...prev,
      nationality: value,
      nationalityCode: ''
    }));

    if (value.length > 0 && !showNationalityDropdown) {
      setShowNationalityDropdown(true);
    }
  };

  // Handle nationality selection
  const handleNationalitySelect = (country) => {
    setFormData(prev => ({
      ...prev,
      nationality: country.name,
      nationalityCode: country.countryid.toString()
    }));
    setNationalitySearchTerm('');
    setShowNationalityDropdown(false);
  };

  // Handle nationality input focus
  const handleNationalityFocus = () => {
    setShowNationalityDropdown(true);
    setNationalitySearchTerm('');
  };

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.city-input-container')) {
        setShowCityDropdown(false);
      }
      if (!event.target.closest('.nationality-input-container')) {
        setShowNationalityDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Parse date string to Date object
  const parseDate = (dateStr) => {
    const [day, month, year] = dateStr.split(' ');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthIndex = monthNames.indexOf(month);
    return new Date(year, monthIndex, parseInt(day));
  };

  // Format date to "YYYY-MM-DD"
  const formatDateForAPI = (dateStr) => {
    const date = parseDate(dateStr);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };


  // Check if checkout date is valid (not before checkin)
  const isValidCheckoutDate = (checkOutDate) => {
    const checkInDate = parseDate(formData.checkIn);
    return checkOutDate >= checkInDate;
  };

  // Get month name
  const getMonthName = (monthIndex) => {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return monthNames[monthIndex];
  };

  // Generate calendar data for a specific month
  const generateCalendarData = (month, year) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days = [];
    let week = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDay; i++) {
      const prevMonth = month === 0 ? 11 : month - 1;
      const prevYear = month === 0 ? year - 1 : year;
      const prevMonthLastDay = new Date(year, month, 0).getDate();
      week.push({
        day: prevMonthLastDay - startingDay + i + 1,
        month: prevMonth,
        year: prevYear,
        isCurrentMonth: false
      });
    }

    // Add days of current month
    for (let day = 1; day <= daysInMonth; day++) {
      week.push({
        day,
        month,
        year,
        isCurrentMonth: true
      });

      if (week.length === 7) {
        days.push([...week]);
        week = [];
      }
    }

    // Add days from next month to complete the last week
    if (week.length > 0) {
      const nextMonth = month === 11 ? 0 : month + 1;
      const nextYear = month === 11 ? year + 1 : year;
      let nextMonthDay = 1;
      while (week.length < 7) {
        week.push({
          day: nextMonthDay,
          month: nextMonth,
          year: nextYear,
          isCurrentMonth: false
        });
        nextMonthDay++;
      }
      days.push([...week]);
    }

    return days;
  };

  // Navigate to previous month
  const goToPreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  // Navigate to next month
  const goToNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  // Check if a month is before current month (should be disabled)
  const isMonthDisabled = (month, year) => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();

    return year < currentYear || (year === currentYear && month < currentMonth);
  };

  const handleDateSelect = (day, month, year, isCheckIn = false) => {
    const newDate = new Date(year, month, day);
    const formattedDate = formatDate(newDate);

    if (isCheckIn) {
      setFormData(prev => {
        const checkInDate = newDate;
        const checkOutDate = parseDate(prev.checkOut);

        if (checkInDate) {
          const nextDay = new Date(checkInDate);
          nextDay.setDate(nextDay.getDate() + 1);
          return {
            ...prev,
            checkIn: formattedDate,
            checkOut: formatDate(nextDay)
          };
        } else {
          return { ...prev, checkIn: formattedDate };
        }
      });
      setShowCheckInCalendar(false);
    } else {
      if (isValidCheckoutDate(newDate)) {
        setFormData(prev => ({ ...prev, checkOut: formattedDate }));
        setShowCheckOutCalendar(false);
      }
    }
  };

  const toggleRating = (rating) => {
    setFormData(prev => {
      const updatedRatings = prev.selectedRatings.includes(rating)
        ? prev.selectedRatings.filter(r => r !== rating)
        : [...prev.selectedRatings, rating];

      return { ...prev, selectedRatings: updatedRatings.sort() };
    });
  };

  const handleRoomChange = (roomIndex, field, value) => {
    setFormData(prev => {
      const updatedRooms = [...prev.rooms];

      if (field === 'adults') {
        updatedRooms[roomIndex].adults = Math.max(1, Math.min(40, value));
      } else if (field === 'children') {
        const childrenCount = Math.max(0, Math.min(4, value));
        updatedRooms[roomIndex].children = childrenCount;

        const currentAges = updatedRooms[roomIndex].childAge || [];
        if (childrenCount > currentAges.length) {
          while (updatedRooms[roomIndex].childAge.length < childrenCount) {
            updatedRooms[roomIndex].childAge.push(1);
          }
        } else {
          updatedRooms[roomIndex].childAge = currentAges.slice(0, childrenCount);
        }
      }

      return { ...prev, rooms: updatedRooms };
    });
  };

  const handleChildAgeChange = (roomIndex, childIndex, age) => {
    setFormData(prev => {
      const updatedRooms = [...prev.rooms];
      updatedRooms[roomIndex].childAge[childIndex] = Math.max(1, Math.min(17, age));
      return { ...prev, rooms: updatedRooms };
    });
  };

  const addRoom = () => {
    if (formData.rooms.length < 9) {
      setFormData(prev => ({
        ...prev,
        rooms: [
          ...prev.rooms,
          {
            adults: 2,
            children: 0,
            childAge: []
          }
        ]
      }));
    }
  };

  const removeRoom = (roomIndex) => {
    if (formData.rooms.length > 1) {
      setFormData(prev => ({
        ...prev,
        rooms: prev.rooms.filter((_, index) => index !== roomIndex)
      }));
    }
  };

  const getTotalGuests = () => {
    return formData.rooms.reduce((total, room) => {
      return total + room.adults + room.children;
    }, 0);
  };

  const getSelectedRatingsText = () => {
    if (formData.selectedRatings.length === 0) return 'Any Rating';
    return formData.selectedRatings.map(r => `${r} Star`).join(', ');
  };

  const applyGuestSelection = () => {
    setShowGuestPopup(false);
  };

  const [HomeStayOnly, setHomeStayOnly] = useState(formData?.HomeStayOnly || false);

  const navigate = useNavigate();
  const HandleHomeStayOnly = (event) => {
    const { value, checked } = event.target;
    setHomeStayOnly(checked)
  }

  const handleShowHotels = (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.city) {
      toast.error('Please select a city');
      return false;
    }
    if (formData.selectedRatings && formData.selectedRatings.length == 0) {
      toast.error('Please Select Hotel Rating');
      return false;
    }
    if (!formData.nationality) {
      toast.error('Please select a nationality');
      return false;
    }

    if (!formData.checkIn || !formData.checkOut) {
      toast.error('Please select check-in and check-out dates');
      return false;
    }

    // Function to convert date format from "10 Oct 2025" to "2025-10-10"
    const formatDate = (dateString) => {
      const months = {
        'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04',
        'May': '05', 'Jun': '06', 'Jul': '07', 'Aug': '08',
        'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
      };

      const parts = dateString.split(' ');
      const day = parts[0].padStart(2, '0');
      const month = months[parts[1]];
      const year = parts[2];

      return `${year}-${month}-${day}`;
    };


    // Calculate totals
    const totalAdults = formData.rooms.reduce((sum, room) => sum + room.adults, 0);
    const totalChildren = formData.rooms.reduce((sum, room) => sum + room.children, 0);
    const totalGuests = totalAdults + totalChildren;

    const params = new URLSearchParams({
      cityname: encodeURIComponent(formData.city),
      checkin_date: formatDate(formData.checkIn),
      checkout_date: formatDate(formData.checkOut),
      nationality: formData.nationalityCode,
      guest: `${formData.rooms.length} Rooms & ${totalGuests} Guest`,
      adults: totalAdults.toString(),
      childs: totalChildren.toString(),
      cityId: formData.cityCode,
      is_city: formData.is_city ? 1 : 0,
      hotel_id: formData?.hotel_id ?? 0,
      interNationalSearch: formData.countryName == 'INDIA' ? false : true,
      hotel_countryCode: formData.countryName,
      // code: "IN",
      hotelRating: formData.selectedRatings.join(','),
      rooms: formData.rooms.length.toString(),
      HomeStayOnly: HomeStayOnly,
    });


    formData.rooms.forEach((room, index) => {
      params.append(`room${index + 1}_adults`, room.adults.toString());
      params.append(`room${index + 1}_children`, room.children.toString());


      room.childAge.forEach((age, childIndex) => {
        params.append(`room${index + 1}_child${childIndex + 1}_age`, age.toString());
      });


      for (let i = room.childAge.length; i < 2; i++) {
        params.append(`room${index + 1}_child${i + 1}_age`, '');
      }
    });

    const queryString = params.toString();
    window.location.href = `/hotel/search?${queryString}`;

    return false;
  };
  const Calendar = ({ onDateSelect, selectedDate, isCheckIn = false }) => {
    const checkInDate = parseDate(formData.checkIn);
    const checkOutDate = parseDate(formData.checkOut);
    const currentDate = new Date();

    const calendarData = generateCalendarData(currentMonth, currentYear);
    const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
    const nextMonthYear = currentMonth === 11 ? currentYear + 1 : currentYear;

    const isDateSelected = (day, month, year) => {
      const date = new Date(year, month, day);
      const selected = parseDate(selectedDate);
      return date.toDateString() === selected.toDateString();
    };

    const isDateInRange = (day, month, year) => {
      const date = new Date(year, month, day);
      return date >= checkInDate && date <= checkOutDate;
    };

    const isDateDisabled = (day, month, year, isCurrentMonth) => {
      if (!isCurrentMonth) return true;

      const date = new Date(year, month, day);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (isCheckIn) {
        return date < today;
      } else {
        return date < checkInDate;
      }
    };

    return (
      <div className="w-80">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={goToPreviousMonth}
            disabled={isMonthDisabled(currentMonth === 0 ? 11 : currentMonth - 1, currentMonth === 0 ? currentYear - 1 : currentYear)}
            className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div className="text-lg font-semibold">
            {getMonthName(currentMonth)} {currentYear}
          </div>

          <button
            onClick={goToNextMonth}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 text-sm">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
            <div key={day} className="text-center font-medium py-2 text-slate-600 text-xs">
              {day}
            </div>
          ))}

          {calendarData.flat().map((dateObj, index) => (
            <div
              key={index}
              onClick={() => {
                if (!isDateDisabled(dateObj.day, dateObj.month, dateObj.year, dateObj.isCurrentMonth)) {
                  onDateSelect(dateObj.day, dateObj.month, dateObj.year, isCheckIn);
                }
              }}
              className={`p-2 text-center cursor-pointer rounded text-sm ${!dateObj.isCurrentMonth ? 'text-slate-300' : ''
                } ${isDateDisabled(dateObj.day, dateObj.month, dateObj.year, dateObj.isCurrentMonth)
                  ? 'text-slate-300 cursor-not-allowed'
                  : isDateSelected(dateObj.day, dateObj.month, dateObj.year)
                    ? 'bg-[#1877F2] text-white hover:bg-[#1461cf]'
                    : isDateInRange(dateObj.day, dateObj.month, dateObj.year)
                      ? 'bg-[#1877F2]/10 text-[#1877F2] hover:bg-[#1877F2]/20'
                      : 'hover:bg-slate-100'
                }`}
            >
              {dateObj.day}
            </div>
          ))}
        </div>

        {/* <div className="mt-6">
          <div className="text-lg font-semibold mb-3 text-center">
            {getMonthName(nextMonth)} {nextMonthYear}
          </div>

          <div className="grid grid-cols-7 gap-1 text-sm">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
              <div key={day} className="text-center font-medium py-2 text-slate-600 text-xs">
                {day}
              </div>
            ))}

            {generateCalendarData(nextMonth, nextMonthYear).flat().map((dateObj, index) => (
              <div
                key={index}
                onClick={() => {
                  if (!isDateDisabled(dateObj.day, dateObj.month, dateObj.year, dateObj.isCurrentMonth)) {
                    onDateSelect(dateObj.day, dateObj.month, dateObj.year, isCheckIn);
                  }
                }}
                className={`p-2 text-center cursor-pointer rounded text-sm ${!dateObj.isCurrentMonth ? 'text-slate-300' : ''
                  } ${isDateDisabled(dateObj.day, dateObj.month, dateObj.year, dateObj.isCurrentMonth)
                    ? 'text-slate-300 cursor-not-allowed'
                    : isDateSelected(dateObj.day, dateObj.month, dateObj.year)
                      ? 'bg-[#1877F2] text-white hover:bg-[#1461cf]'
                      : isDateInRange(dateObj.day, dateObj.month, dateObj.year)
                        ? 'bg-[#1877F2]/10 text-[#1877F2] hover:bg-[#1877F2]/20'
                        : 'hover:bg-slate-100'
                  }`}
              >
                {dateObj.day}
              </div>
            ))}
          </div>
        </div> */}
      </div>
    );
  };

  // Reset calendar to current month when opening
  useEffect(() => {
    if (showCheckInCalendar) {
      let checkinDate = new Date();
      if (formData.checkIn) {
        checkinDate = new Date(formData.checkIn)
      }
      setCurrentMonth(checkinDate.getMonth());
      setCurrentYear(checkinDate.getFullYear());
    }
    if (showCheckOutCalendar) {
      const checkin_date = new Date(formData.checkIn);
      setCurrentMonth(checkin_date.getMonth());
      setCurrentYear(checkin_date.getFullYear());
    }
  }, [showCheckInCalendar, showCheckOutCalendar]);

  return (
    <div className={`rounded-2xl border border-slate-100 bg-white p-4 shadow-lg sm:p-4.5 ${className}`}>
      <form onSubmit={handleShowHotels}>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
          {/* City Input */}
          <div className="city-input-container relative md:col-span-5">
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Enter City/Hotel Name
            </label>
            <input
              type="text"
              value={formData.city}
              onChange={handleCityInputChange}
              onFocus={handleCityFocus}
              placeholder="Enter City Name"
              className="w-full rounded-xl border border-slate-200 p-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#1877F2]"
            />
            {showCityDropdown && (
              <div className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg">
                {cityLoading ? (
                  <div className="px-4 py-2 text-center text-slate-500">
                    Searching...
                  </div>
                ) : filteredCities.length === 0 ? (
                  <div className="px-4 py-2 text-center text-slate-500">
                    No cities found
                  </div>
                ) : (
                  filteredCities.map(city => (
                    <div
                      key={city.id}
                      onClick={() => handleCitySelect(city)}
                      className="cursor-pointer border-b border-slate-100 px-4 py-2 last:border-b-0 hover:bg-slate-50"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-2">
                          {!city.is_city && (
                            <Hotel className="mt-1 h-3.5 w-3.5 shrink-0 text-slate-500" />
                          )}

                          <div className="min-w-0">
                            <div className="break-words font-medium text-slate-800">
                              {city.name}
                            </div>
                            <div className="break-words text-sm text-slate-500">
                              {city.fullRegionName}
                            </div>
                          </div>
                        </div>
                        <span className="rounded-full bg-[#1877F2]/10 px-2 py-1 text-xs text-[#1877F2]">
                          {city.countryName}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Check In / Check Out */}
          <div className="grid grid-cols-2 gap-2 md:col-span-4">
            <div className="relative">
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Check In
              </label>
              <div
                className="w-full cursor-pointer truncate rounded-xl border border-slate-200 p-2.5 text-sm transition-colors hover:border-slate-300"
                onClick={() => {
                  setShowCheckInCalendar(true);
                  setShowCheckOutCalendar(false);
                }}
              >
                {formData.checkIn}
              </div>
            </div>

            <div className="relative">
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Check Out
              </label>
              <div
                className="w-full cursor-pointer truncate rounded-xl border border-slate-200 p-2.5 text-sm transition-colors hover:border-slate-300"
                onClick={() => {
                  setShowCheckOutCalendar(true);
                  setShowCheckInCalendar(false);
                }}
              >
                {formData.checkOut}
              </div>
            </div>
          </div>

          {/* Rooms & Guest Selector */}
          <div className="relative md:col-span-3">
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Rooms & Guest
            </label>
            <div
              className="w-full cursor-pointer truncate rounded-xl border border-slate-200 p-2.5 text-sm transition-colors hover:border-slate-300"
              onClick={() => setShowGuestPopup(true)}
            >
              {formData.rooms.length} Rooms & {getTotalGuests()} Guest{getTotalGuests() !== 1 ? 's' : ''}
            </div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 sm:gap-4">
          {/* Nationality */}
          <div className="nationality-input-container relative">
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Nationality
            </label>
            <input
              type="text"
              value={formData.nationality}
              onChange={handleNationalityInputChange}
              onFocus={handleNationalityFocus}
              placeholder="Enter Nationality"
              className="w-full rounded-xl border border-slate-200 p-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#1877F2]"
            />
            {showNationalityDropdown && (
              <div className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg">
                {filteredNationalities.length === 0 ? (
                  <div className="px-4 py-2 text-center text-slate-500">
                    No nationalities found
                  </div>
                ) : (
                  filteredNationalities.map(country => (
                    <div
                      key={country.code}
                      onClick={() => handleNationalitySelect(country)}
                      className="cursor-pointer border-b border-slate-100 px-4 py-2 last:border-b-0 hover:bg-slate-50"
                    >
                      {country.name}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Rating */}
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Selected Rating {formData.selectedRatings.join(", ")}</label>
            <div className="flex flex-wrap gap-2">
              {ratings.map(rating => (
                <button
                  key={rating}
                  type="button"
                  onClick={() => toggleRating(rating)}
                  className={`flex items-center gap-1 rounded-lg border px-2.5 py-2 text-xs font-semibold transition-colors ${formData.selectedRatings.includes(rating)
                    ? 'border-[#1877F2] bg-[#1877F2] text-white shadow-sm'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                >
                  <span>⭐</span>
                  <span>{rating}</span>
                </button>
              ))}
            </div>
          </div>

        </div>

        <div className="md:col-span-2 lg:col-span-2 gap-2 grid grid-cols-3 mt-2">
          {(user?.UserData?.user?.AgencyID === 97 || user?.UserData?.user?.AgencyID === 42) && (
            <div>
              <div className="flex items-center border rounded-lg p-3 hover:bg-gray-50">
                <input
                  checked={HomeStayOnly === true || HomeStayOnly === 'true'}
                  value="1"
                  type="checkbox"
                  onClick={HandleHomeStayOnly}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-3 text-sm font-medium text-gray-700">
                  Only Homestay &nbsp;
                  <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                    NEW
                  </span>
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Action Button */}
        <button
          type="submit"
          className="mt-4 flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-[#1877F2] px-5 py-2.5 text-sm font-bold uppercase tracking-wide text-white shadow-md shadow-[#1877F2]/25 transition-colors hover:bg-[#1461cf] active:bg-[#1461cf] disabled:cursor-not-allowed disabled:opacity-70"
        >
          Search Hotels
        </button>
      </form>

      {/* Rooms & Guests Popup Modal */}
      {showGuestPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-xl md:w-full md:max-w-md">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-slate-100 p-3">
              <h3 className="text-lg font-semibold text-slate-800">Select Rooms & Guests</h3>
              <button
                onClick={() => setShowGuestPopup(false)}
                className="rounded-full p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {/* w-full h-full bg-white flex flex-col shadow-2xl md:h-auto md:max-h-[90vh] md:w-full md:max-w-md md:rounded-2xl */}
            {/* Content */}
            <div className="p-2">
              {formData.rooms.map((room, roomIndex) => (
                <div key={roomIndex} className="mb-2 last:mb-0 p-2 border border-slate-100 rounded-lg bg-slate-50">
                  <div className="flex justify-between items-center">
                    <h4 className="font-semibold text-lg text-slate-800">Room {roomIndex + 1}</h4>
                    {roomIndex > 0 && (
                      <button
                        type="button"
                        onClick={() => removeRoom(roomIndex)}
                        className="px-2 py-1 text-sm bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors font-medium"
                      >
                        <X />
                      </button>
                    )}
                  </div>

                  {/* Adults */}
                  <div className="mb-2">
                    <label className="block text-sm font-medium text-slate-700 mb-3">
                      Adults (18+ Years)
                    </label>
                    <div className="flex items-center gap-4">
                      <button
                        type="button"
                        onClick={() => handleRoomChange(roomIndex, 'adults', room.adults - 1)}
                        disabled={room.adults <= 1}
                        className="w-12 h-8 flex items-center justify-center border border-slate-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100 transition-colors text-lg font-bold"
                      >
                        -
                      </button>
                      <span className="w-12 text-center font-bold text-xl text-slate-800">{room.adults}</span>
                      <button
                        type="button"
                        onClick={() => handleRoomChange(roomIndex, 'adults', room.adults + 1)}
                        disabled={room.adults >= 9}
                        className="w-12 h-8 flex items-center justify-center border border-slate-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100 transition-colors text-lg font-bold"
                      >
                        +
                      </button>
                    </div>
                    <div className="mt-2 text-xs text-slate-500">
                      Maximum 9 adults per room
                    </div>
                  </div>

                  {/* Children */}
                  <div className="mb-2">
                    <label className="block text-sm font-medium text-slate-700 mb-3">
                      Children (0-17 years old)
                    </label>
                    <div className="flex items-center gap-4">
                      <button
                        type="button"
                        onClick={() => handleRoomChange(roomIndex, 'children', room.children - 1)}
                        disabled={room.children <= 0}
                        className="w-12 h-8 flex items-center justify-center border border-slate-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100 transition-colors text-lg font-bold"
                      >
                        -
                      </button>
                      <span className="w-12 text-center font-bold text-xl text-slate-800">{room.children}</span>
                      <button
                        type="button"
                        onClick={() => handleRoomChange(roomIndex, 'children', room.children + 1)}
                        disabled={room.children >= 4}
                        className="w-12 h-8 flex items-center justify-center border border-slate-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100 transition-colors text-lg font-bold"
                      >
                        +
                      </button>
                    </div>
                    <div className="mt-2 text-xs text-slate-500">
                      Maximum 4 children per room
                    </div>
                  </div>

                  {/* Child Ages */}
                  {room.children > 0 && (
                    <div className="">
                      <label className="block text-sm font-medium text-slate-700 mb-3">
                        Children Ages
                      </label>
                      <div className="grid grid-cols-4 gap-4">
                        {Array.from({ length: room.children }, (_, childIndex) => (
                          <div key={childIndex} className="bg-white p-2 rounded-lg border border-slate-100">
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                              Child {childIndex + 1}
                            </label>
                            <select
                              value={room.childAge[childIndex] || 0}
                              onChange={(e) => handleChildAgeChange(roomIndex, childIndex, parseInt(e.target.value))}
                              className="w-full p-1 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#1877F2] focus:border-transparent bg-white"
                            >
                              {Array.from({ length: 17 }, (_, i) => i + 1).map(age => (
                                <option key={age} value={age}>
                                  {age} Yr{age !== 1 ? 's' : ''}
                                </option>
                              ))}
                            </select>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Add Another Room */}
              {formData.rooms.length < 9 && (
                <button
                  type="button"
                  onClick={addRoom}
                  className="w-full py-1 border-2 border-dashed border-slate-300 rounded-lg text-slate-600 hover:bg-slate-50 hover:border-slate-400 transition-colors font-semibold text-sm"
                >
                  + Add Another Room
                </button>
              )}

              {/* Summary */}
              {/* <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-[#1877F2]/30">
                <div className="text-sm text-[#1877F2]">
                  <div className="font-semibold">Summary:</div>
                  <div>{formData.rooms.length} Room{formData.rooms.length !== 1 ? 's' : ''}</div>
                  <div>{getTotalGuests()} Guest{getTotalGuests() !== 1 ? 's' : ''}</div>
                </div>
              </div> */}
            </div>

            {/* Footer */}
            <div className="flex gap-4 p-2 border-t border-slate-100 bg-slate-50 rounded-b-lg">
              <button
                type="button"
                onClick={() => setShowGuestPopup(false)}
                className="px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-100 transition-colors font-semibold flex-1"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyGuestSelection}
                className="px-4 py-2 bg-[#1877F2] text-white rounded-xl hover:bg-[#1461cf] transition-colors font-semibold flex-1"
              >
                Select
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Check In Calendar Modal */}
      {showCheckInCalendar && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-2xl shadow-xl">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-lg font-semibold">Check In Date</h3>
              <button
                onClick={() => setShowCheckInCalendar(false)}
                className="text-slate-500 hover:text-slate-700 text-2xl"
              >
                ×
              </button>
            </div>
            <Calendar
              onDateSelect={handleDateSelect}
              selectedDate={formData.checkIn}
              isCheckIn={true}
            />
          </div>
        </div>
      )}

      {/* Check Out Calendar Modal */}
      {showCheckOutCalendar && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-2xl shadow-xl">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-lg font-semibold"> Check Out Date</h3>
              <button
                onClick={() => setShowCheckOutCalendar(false)}
                className="text-slate-500 hover:text-slate-700 text-2xl"
              >
                ×
              </button>
            </div>
            <Calendar
              onDateSelect={handleDateSelect}
              selectedDate={formData.checkOut}
              isCheckIn={false}
            />
            {/* <div className="mt-4 text-sm text-slate-600">
              <p>Check-out date cannot be earlier than check-in date ({formData.checkIn})</p>
            </div> */}
          </div>
        </div>
      )}
    </div>
  );
};

export default HotelSearchForm;